import { useMutation } from '@apollo/client/react';
import { Button } from '@optools/components/ui/button';
import { Checkbox } from '@optools/components/ui/checkbox';
import { Label } from '@optools/components/ui/label';
import { useBrowserStorage } from '@optools/hooks/useBrowserStorage';
import { END_AD_BREAK, INSERT_AD_BREAK } from '@optools/services/operator-gateway/logger/mutations';
import { GET_LOGGER_GAME } from '@optools/services/operator-gateway/logger/queries';
import { LoggerGamePipeline } from '@optools/services/operator-gateway/logger/types';
import {
  AdEventType,
  MediaFeedType,
  StreamState,
} from '@optools/services/operator-gateway/types/generated/graphql';
import { SlateCodes } from '@optools/services/operator-gateway/types/slate';
import { selectLoggerConfig } from '@optools/store/configApiSlice';
import { useAppSelector } from '@optools/store/hooks';
import { useParams, useSearch } from '@tanstack/react-router';
import { addSeconds, differenceInSeconds } from 'date-fns';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';

import { getIsInAdBreak } from '../utils/ad-break';
import { determineNotificationPosition, VerticalPosition } from '../utils/notification';
import { AdDurationSelect } from './ad-duration-select';
import { AdEventTypeSelect } from './ad-event-types-select';

export interface AdControlsProps {
  debugMode?: boolean;
  disabled?: boolean;
  mediaId: string;
  pipeline: LoggerGamePipeline;
  side: MediaFeedType;
}

export const DEFAULT_AD_DURATION = 120;

export const AD_EVENT_TYPES = {
  DEFAULT: { label: 'None', value: 'NONE' },
  POST_GAME: { label: 'Post-Game', value: 'POST_GAME' },
  PRE_GAME: { label: 'Pre-Game', value: 'PRE_GAME' },
} as const;

export const determineAdEventType = (
  activeSlateCode: LoggerGamePipeline['activeSlateCode'],
  streamState: LoggerGamePipeline['streamState'],
) => {
  if (streamState === StreamState.STREAM_ON && activeSlateCode !== SlateCodes.PSB) {
    return AD_EVENT_TYPES.PRE_GAME;
  } else if (streamState === StreamState.PROGRAM_DONE && activeSlateCode !== SlateCodes.TYFW) {
    return AD_EVENT_TYPES.POST_GAME;
  } else {
    return AD_EVENT_TYPES.DEFAULT;
  }
};

export function AdControls({
  debugMode = false,
  disabled = false,
  mediaId,
  pipeline,
  side,
}: AdControlsProps) {
  const { commercialPrePostSlateCode, commercialSlateCode, endAdBreakEnabled } =
    useAppSelector(selectLoggerConfig);
  const { activeSlateCode, adBreakDurationInSeconds, adBreakStartedDateTime, streamState } =
    pipeline || {};
  const isAdPlaying = getIsInAdBreak(adBreakStartedDateTime, adBreakDurationInSeconds);
  const { date } = useSearch({ from: '/_auth/logger/$gamePk' });
  const { gamePk } = useParams({ from: '/_auth/logger/$gamePk' });

  const [adDuration, setAdDuration] = useState(DEFAULT_AD_DURATION);
  const [adEventType, setAdEventType] = useState<AdEventType>(AD_EVENT_TYPES.DEFAULT);
  const [adRemaining, setAdRemaining] = useState<number>(0);
  const [includeBuffer, setIncludeBuffer] = useBrowserStorage(
    'sessionStorage',
    `${pipeline.id}-includeBuffer`,
    false,
  );

  useEffect(() => {
    let timeoutId: ReturnType<typeof setTimeout>;
    let intervalId: ReturnType<typeof setInterval>;

    if (isAdPlaying && adBreakDurationInSeconds && adBreakStartedDateTime) {
      const endTime = addSeconds(new Date(adBreakStartedDateTime), adBreakDurationInSeconds);

      const updateRemainingTime = () => {
        const remainingSecs = Math.max(0, differenceInSeconds(endTime, new Date()));
        setAdRemaining(remainingSecs);

        if (remainingSecs <= 0 && intervalId) {
          clearInterval(intervalId);
        }
      };

      // Immediately update the time before the timer logic starts to avoid lost second during countdown
      updateRemainingTime();

      // Aligns the timer with the exact second
      const msUntilNextSecond = 1000 - new Date().getMilliseconds();

      timeoutId = setTimeout(() => {
        updateRemainingTime();
        intervalId = setInterval(updateRemainingTime, 1000);
      }, msUntilNextSecond);
    } else {
      setAdRemaining(0);
    }

    return () => {
      if (timeoutId) clearTimeout(timeoutId);
      if (intervalId) clearInterval(intervalId);
    };
  }, [isAdPlaying, adBreakDurationInSeconds, adBreakStartedDateTime]);

  const [insertAdBreak, insertAdStatus] = useMutation(INSERT_AD_BREAK, {
    awaitRefetchQueries: true,
    onCompleted: (data) => {
      const success = data?.insertAdBreak?.[0]?.success;
      const reason = data?.insertAdBreak?.[0]?.reason;
      if (!success) {
        toast.error('Insert Ad Break Error', {
          description: reason || 'A server error occurred when trying to insert the ad break',
          duration: Infinity,
          position: determineNotificationPosition(side, VerticalPosition.BOTTOM),
        });
        return;
      }
    },
    refetchQueries: [{ query: GET_LOGGER_GAME, variables: { gamePk, scheduleDate: date } }],
  });

  const [endAdBreak, endAdStatus] = useMutation(END_AD_BREAK, {
    awaitRefetchQueries: true,
    onCompleted: (data) => {
      const success = data?.endAdBreak?.[0]?.success;
      const reason = data?.endAdBreak?.[0]?.reason;
      if (!success) {
        toast.error('End Ad Break Error', {
          description: reason || 'A server error occurred when trying to end the ad break',
          duration: Infinity,
          position: determineNotificationPosition(side, VerticalPosition.BOTTOM),
        });
        return;
      }
    },
    refetchQueries: [{ query: GET_LOGGER_GAME, variables: { gamePk, scheduleDate: date } }],
  });

  useEffect(() => {
    const adEventType = determineAdEventType(activeSlateCode, streamState);
    setAdEventType(adEventType);
  }, [activeSlateCode, streamState]);

  const startAdBreak = () => {
    const isPrePost =
      adEventType.value === AD_EVENT_TYPES.PRE_GAME.value ||
      adEventType.value === AD_EVENT_TYPES.POST_GAME.value;

    insertAdBreak({
      variables: {
        durationSeconds: adDuration,
        eventType: adEventType.value,
        includeBuffer,
        mediaIds: [mediaId],
        slateCode: isPrePost ? commercialPrePostSlateCode : commercialSlateCode,
      },
    });
  };

  const stopAdBreak = () => {
    endAdBreak({ variables: { mediaIds: [mediaId] } });
  };

  // The config value specifies that this button should not be disabled during active ad breaks
  const disabledEndAdBreak = endAdBreakEnabled ? false : !isAdPlaying;

  return (
    <div
      className="flex flex-col items-center"
      data-testid={`pipeline-ad-controls-${pipeline.id}-${mediaId}`}>
      <p className="pb-2 text-sm font-bold">Ad Break Controls</p>
      <div className="flex flex-col gap-2">
        {debugMode ? (
          <p className="mb-1 rounded-sm border border-dashed p-1 text-sm">
            Active Ad Break? {isAdPlaying ? 'YES' : 'NO'}
          </p>
        ) : null}
        <AdEventTypeSelect
          adEventType={adEventType}
          disabled={disabled}
          setAdEventType={setAdEventType}
        />
        {isAdPlaying && adRemaining > 0 ? (
          <div className="flex flex-col gap-1">
            <p className="text-xs font-medium">Ad Duration</p>
            <div className="flex h-10 items-center gap-1 rounded-sm border px-3 py-2 text-sm">
              <p className="tabular-nums" data-testid="remaining-time">
                {adRemaining}
              </p>
              <span>Second{adRemaining !== 1 && 's'} Left</span>
            </div>
          </div>
        ) : (
          <AdDurationSelect
            adDuration={adDuration}
            disabled={disabled}
            setAdDuration={setAdDuration}
          />
        )}
        <Label className="my-2 cursor-pointer">
          <Checkbox
            checked={includeBuffer}
            disabled={isAdPlaying || endAdStatus.loading || insertAdStatus.loading}
            onCheckedChange={() => setIncludeBuffer((value) => !value)}
          />
          Extend Ad Break
        </Label>
        <div>
          <h3 className="mb-2 text-xs font-medium">Actions</h3>

          <div className="flex gap-2">
            <Button
              disabled={isAdPlaying || disabled || endAdStatus.loading || insertAdStatus.loading}
              variant="outline"
              onClick={startAdBreak}>
              Start Ad Break
            </Button>
            <Button
              disabled={
                disabledEndAdBreak || disabled || endAdStatus.loading || insertAdStatus.loading
              }
              variant="outline"
              onClick={stopAdBreak}>
              End Ad Break
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
